import { deleteDoublons } from "../doublon-tools"
import { normalizeString } from "../string"
import { ITreeNode } from "./interfaces"

// Helper functions for filtering
export const defaultMatcher = (
  filterText: string, // | string[],
  node: ITreeNode
): boolean => {
  const nodeName = node.name ? normalizeString(node.name) : ""
  const filter = normalizeString(filterText as string)
  return nodeName.includes(filter)
}

export const caracMatcher =
  (carac: string) =>
  (
    filterText: string, // | string[],
    node: ITreeNode
  ): boolean => {
    console.log(
      node.name,
      node.declinableCodeEnums,
      node.declinableCodeEnums?.includes(carac),
      carac
    )
    return node.declinableCodeEnums?.includes(carac) || false
  }

export const nodeExists = (
  node: ITreeNode,
  filter: string,
  matcher = defaultMatcher
): boolean => {
  return (
    matcher(filter, node) ||
    (node.children !== undefined &&
      !!node.children.find((child: ITreeNode) =>
        nodeExists(child, filter, matcher)
      ))
  )
}

export const filterTree = (
  node: ITreeNode,
  filter = "",
  matcher = defaultMatcher
): ITreeNode => {
  // If im an exact match then all my children get to stay
  if (matcher(filter, node) || !node.children) {
    return node
  }
  // If not then only keep the ones that match or have matching descendants
  const filtered: ITreeNode[] = node.children
    .filter((child) => nodeExists(child, filter, matcher))
    .map((child) => filterTree(child, filter, matcher))

  const result = { ...node, children: filtered }
  return result
}

export const expandFilteredNodes = (
  node: ITreeNode,
  filter: string | string[],
  callback: (nodesToExpand: string[]) => void,
  matcher = defaultMatcher
) => {
  if (typeof filter === "object") {
    const result = filter.reduce((acc: any, o) => {
      return deleteDoublons([...acc, ...recursiveExp(node, acc, o, matcher)])
    }, [])
    callback(result)
  } else {
    callback(recursiveExp(node, [], filter, matcher))
  }
}

const recursiveExp = (
  node: ITreeNode,
  nodesToExpand: string[],
  f: string,
  matcher: (s: string, node: ITreeNode) => boolean
) => {
  const children = node.children
  if (children && children.length !== 0) {
    nodesToExpand.push(node.value)
    const childrenWithMatches =
      node.children === undefined
        ? []
        : node.children.filter((child) => nodeExists(child, f, matcher))
    const shouldExpand = childrenWithMatches.length > 0
    if (shouldExpand) {
      childrenWithMatches.forEach((n) =>
        recursiveExp(n, nodesToExpand, f, matcher)
      )
    }
  }
  return nodesToExpand
}

export const multiFilterTree = ({
  node,
  filter = "",
  matchers = [defaultMatcher],
}: IMultiFilterTreeProps): Promise<ITreeNode> =>
  new Promise<ITreeNode>((resolve, reject) => {
    const keepNode = matchers.reduce(
      (acc, matcher) => acc && matcher(filter, node),
      true
    )
    // If im an exact match then all my children get to stay
    if (keepNode || !node.children) {
      return resolve(node)
    }
    // If not then only keep the ones that match or have matching descendants
    const filtered: Array<Promise<ITreeNode>> = node.children
      .filter((child) => multiNodeExists(child, filter, matchers))
      .map((child) => multiFilterTree({ node: child, filter, matchers }))

    return resolve(
      Promise.all(filtered).then((filts: ITreeNode[]) => ({
        ...node,
        children: filts,
      }))
    )
  })

export const multiNodeExists = (
  node: ITreeNode,
  filter: string, // | string[],
  matchers = [defaultMatcher]
): boolean => {
  const keepNode = matchers.reduce(
    (acc, matcher) => acc && matcher(filter, node),
    true
  )
  return (
    keepNode ||
    (node.children !== undefined &&
      !!node.children.find((child: ITreeNode) =>
        multiNodeExists(child, filter, matchers)
      ))
  )
}

export interface IMultiFilterTreeProps {
  node: ITreeNode
  filter?: string // | string[]
  matchers?: Array<(f: string /*| string[]*/, node: ITreeNode) => boolean>
}
